<template>
  <sc-page title="菜单设置" desc="设置菜单相关属性，勾选进行展示">
    <div class="menu">
      <div class="menu-tree-box menu-box">
        <sc-tree ref="menu">
          <template v-slot="{ node, children }">
            <menu-node
              :node="node"
              @add="add(node)"
              @data="Object.assign(node.data, $event)"
              @move="onDrag(...$event)"
            />
            <tran-reduce :show="node.opened">
              <div class="children">
                <template v-for="(child, index) in children" :key="index">
                  <component :is="child" />
                </template>
              </div>
            </tran-reduce>
          </template>
        </sc-tree>
      </div>
      <div class="menu-tree-box system-box">
        <sc-tree ref="system">
          <template v-slot="{ node, children }">
            <system-node
              :node="node"
              v-if="node.level !== 0"
              @add="addToRoot"
            />
            <tran-reduce :show="node.opened || node.level === 0">
              <div class="children">
                <component
                  :is="child"
                  v-for="(child, index) in children"
                  :key="index"
                />
              </div>
            </tran-reduce>
          </template>
        </sc-tree>
      </div>
    </div>
    <div class="form-btn-group">
      <sc-btn type="primary" :handler="submit" text="保存" />
      <sc-btn style="margin-left: 10px" :handler="reset" text="重置" />
    </div>
  </sc-page>
</template>

<script lang="ts">
import { DeepReadonly, defineComponent, ref, watch } from 'vue';
import { request } from '@/util/request';
import { message } from 'ant-design-vue';
import ScTree from '@/components/common/tree/ScTree.vue';
import ScTreeNode from '@/components/common/tree/ScTreeNode.vue';
import TranReduce from '@/components/common/transition/TranReduce.vue';
import { DataNode, TreeUtil } from '@/components/common/tree/TreeUtil';
import SystemNode from './component/SystemNode.vue';
import ScDrop from '@/components/common/drag/ScDrop';
import MenuNode from './component/MenuNode.vue';
import _ from 'lodash';
import { useRoute } from 'vue-router';

export default defineComponent({
  name: 'Index',
  components: {
    MenuNode,
    ScDrop,
    SystemNode,
    TranReduce,
    ScTreeNode,
    ScTree,
  },
  data() {
    return {
      origin: [] as { id: string; show: boolean; children: [] }[],
      idMax: 0,
    };
  },
  setup() {
    const menu = ref<InstanceType<typeof ScTree>>();
    const system = ref<InstanceType<typeof ScTree>>();
    const route = useRoute();
    const domain = ref( route.query.domain as string);
    watch(() => route.query.domain, () => domain.value = route.query.domain as string);
    return {
      domain,
      menu,
      system,
    };
  },
  mounted() {
    this.getData();
  },
  watch: {
    domain() {
      this.getData();
    },
  },
  methods: {
    async getData() {
      if (!this.domain) {
        return;
      }
      const res = await request('develop/menu/index', { domain: this.domain });
      if (res) {
        this.origin = res.menu;
        if (this.system) {
          this.system.setData(
            TreeUtil.mapNode(res.system, (item) => {
              return {
                input: { id: item.id, data: { ...item, children: undefined } },
                children: item.children,
              };
            })
          );
        } else {
          console.error('未找到system控件');
        }
      }
      this.reset();
    },
    createId() {
      return Math.floor(Math.random() * 1000000000).toString();
    },
    onDrag(
      ev: { data: string },
      target: DataNode<unknown>,
      pos: 'before' | 'after' | 'inside'
    ) {
      if (ev.data === target.id) {
        return;
      }
      if (this.menu) {
        this.menu.moveNodeTo(ev.data, target.id, pos);
        if (pos === 'inside') {
          target.setOpen(true);
        } else if (target.parent) {
          target.parent.setOpen(true);
        }
      }
    },
    getMenus(node: DataNode<unknown>) {
      return [
        { title: '添加子菜单', handler: () => this.add(node) },
        { title: '删除菜单', handler: () => node.del() },
      ];
    },
    addToRoot({ title, path }: { title: string; path: string }) {
      this.menu?.getRoot()?.addChild({
        id: this.createId(),
        checked: true,
        data: {
          class: 'AlignLeftOutlined',
          title,
          path,
        },
      });
    },
    add(node: DataNode<unknown>) {
      if (node.level > 1) {
        message.error('不能添加3级菜单');
        return;
      }
      node.addChild({
        id: this.createId(),
        checked: true,
        data: {
          class: 'AlignLeftOutlined',
          title: '新增菜单',
          path: '',
        },
      });
    },
    reset() {
      if (this.menu && this.origin) {
        this.menu.setData({
          id: this.createId(),
          checked: true,
          data: {
            class: 'AlignLeftOutlined',
            title: '管理后台',
            path: '',
          },
          children: _.cloneDeep(this.origin).map((i) =>
            TreeUtil.mapNode(i, (item) => {
              return {
                input: {
                  id: item.id || this.createId(),
                  checked: item.show,
                  data: { ...item, children: undefined },
                },
                children: item.children,
              };
            })
          ),
        });
      } else {
        console.error('未找到menu控件');
      }
    },
    async submit() {
      if (this.menu) {
        const menuRoot = this.menu.getRoot();
        if (menuRoot) {
          const res = await request('develop/menu/save', {
            domain: this.domain,
            menu: this.nodeToData(menuRoot).children,
          });
          if (res) {
            message.success('保存成功');
          }
        }
      } else {
        console.error('未找到menu控件');
      }
    },
    nodeToData(node: DeepReadonly<DataNode<unknown>>): any {
      return {
        ...node.data,
        id: node.id,
        show: node.checked !== 'no',
        children: node.children.map((child) => this.nodeToData(child)),
      };
    },
  },
});
</script>

<style lang="scss" scoped>
.menu {
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  overflow: auto;

  .menu-box {
    flex-grow: 1;
  }

  .system-box {
    width: 600px;
  }

  .menu-tree-box {
    flex-shrink: 0;
    padding: $spacing-medium;

    &:first-child {
      border-right: 1px solid $color-content-border;
    }
  }

  .form {
    margin: $spacing-medium;

    .row {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      margin-bottom: $spacing-medium;

      .label {
        width: 120px;
        margin-right: $spacing-medium;
        text-align: right;
        height: 35px;
        line-height: 35px;
      }

      .row-op {
        width: 100%;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
      }
    }
  }
}

.form-btn-group {
  border-top: 1px solid $color-content-border;
  padding: $spacing-medium;
}
</style>
